import { useRef } from "react"
import { useFrame } from "@react-three/fiber"
import * as THREE from "three"

import {
    ELECTION_PORTAL_CENTER,
    ELECTION_PORTAL_GAP_CENTER,
    ELECTION_PORTAL_GAP_HALF,
    ELECTION_PORTAL_RADIUS,
} from "../../lib/domeConstants"

// 2026-05-06 — Portal de Reconfiguración.
//
// Un segundo anillo, dorado, plantado al lado +X del Domo. El
// Tripulante cruza el aro central por su gap y, al entrar al círculo
// dorado, recibe la opción de "Cambiar firma" → vuelve al rito del
// Estado Cero para elegir un avatar distinto. Reemplaza al menú
// top-right "ELEGIR AVATAR" como gesto in-world: la reconfiguración
// pasa por caminar a un lugar específico, no por un botón flotante.
//
// Visualmente es coherente con el aro central:
//   · Toroide bajo en el piso, dorado intenso pulsando.
//   · Anillo elevado fino que respira al ritmo del bajo.
//   · Apertura del lado -X (centro del Domo) para que el Tripulante
//     entre desde el campo y no tenga que rodear el portal.
//   · Pad cyan en el suelo dentro del círculo, para indicar
//     visualmente que el lugar ES interactivo (mismo lenguaje que
//     el pad central de aterrizaje).

const RING_HEIGHT = 0.4
const BAND = 0.18
const PAD_INNER = 0.4
const PAD_OUTER = 0.95

const THETA_START =
    ELECTION_PORTAL_GAP_CENTER + ELECTION_PORTAL_GAP_HALF
const THETA_LENGTH = Math.PI * 2 - ELECTION_PORTAL_GAP_HALF * 2

export function ElectionPortal() {
    const lowerRef = useRef<THREE.Mesh>(null)
    const upperRef = useRef<THREE.Mesh>(null)
    const padRef = useRef<THREE.Mesh>(null)

    useFrame((state) => {
        const t = state.clock.elapsedTime
        if (lowerRef.current) {
            const m = lowerRef.current
                .material as THREE.MeshStandardMaterial
            m.emissiveIntensity = 1.6 + Math.sin(t * 1.2) * 0.5
        }
        if (upperRef.current) {
            const m = upperRef.current
                .material as THREE.MeshStandardMaterial
            m.emissiveIntensity = 1.2 + Math.sin(t * 1.2 + 0.7) * 0.4
        }
        if (padRef.current) {
            const m = padRef.current
                .material as THREE.MeshStandardMaterial
            m.emissiveIntensity = 0.7 + Math.sin(t * 1.6) * 0.25
            const s = 1 + Math.sin(t * 1.6) * 0.05
            padRef.current.scale.set(s, s, 1)
        }
    })

    return (
        <group
            position={[
                ELECTION_PORTAL_CENTER[0],
                0,
                ELECTION_PORTAL_CENTER[2],
            ]}
        >
            {/* Pad dorado pulsando en el suelo — marca el punto de
                interacción (lectura: aquí pasa algo si paras). */}
            <mesh
                ref={padRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.01, 0]}
            >
                <ringGeometry args={[PAD_INNER, PAD_OUTER, 64]} />
                <meshStandardMaterial
                    color="#000"
                    emissive="#FFD27A"
                    emissiveIntensity={0.7}
                    transparent
                    opacity={0.85}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Aro inferior dorado al ras del piso. */}
            <mesh
                ref={lowerRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.025, 0]}
            >
                <ringGeometry
                    args={[
                        ELECTION_PORTAL_RADIUS - BAND,
                        ELECTION_PORTAL_RADIUS,
                        96,
                        1,
                        THETA_START,
                        THETA_LENGTH,
                    ]}
                />
                <meshStandardMaterial
                    color="#000"
                    emissive="#FFD27A"
                    emissiveIntensity={1.7}
                    transparent
                    opacity={0.95}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Aro superior — marco dorado fino. */}
            <mesh
                ref={upperRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, RING_HEIGHT, 0]}
            >
                <ringGeometry
                    args={[
                        ELECTION_PORTAL_RADIUS - BAND * 0.6,
                        ELECTION_PORTAL_RADIUS,
                        96,
                        1,
                        THETA_START,
                        THETA_LENGTH,
                    ]}
                />
                <meshStandardMaterial
                    color="#000"
                    emissive="#FFE9B5"
                    emissiveIntensity={1.2}
                    transparent
                    opacity={0.85}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Acento de luz dorada interior — refuerza la idea de
                "umbral activo" sin necesidad de bloom adicional. */}
            <pointLight
                position={[0, 0.6, 0]}
                intensity={1.4}
                distance={5}
                color="#FFD27A"
                decay={1.7}
            />
        </group>
    )
}
